/**
 * @fileoverview Frontend Logger
 *
 * Logger simple para el navegador con control por entorno.
 * - Development: Logs visibles en consola del navegador
 * - Staging: NO logs
 * - Production: NO logs
 *
 * @module app/utils/logger
 * @version 1.0.0
 */

/**
 * Log levels
 */
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

/**
 * Logger configuration
 */
interface LoggerConfig {
  module?: string;
  enabled?: boolean;
}

/**
 * Determina si logging está habilitado basado en el entorno
 */
const isLoggingEnabled = (): boolean => {
  // Safely access process.env - might not exist in browser bundles
  const stage = process.env.REACT_APP_STAGE || process.env.NODE_ENV || 'development';
  const isDevelopment = stage === 'development' || stage === 'local' || stage === 'test';

  return isDevelopment;
};

/**
 * Frontend Logger class
 */
export class Logger {
  private module: string;
  private enabled: boolean;

  constructor(config?: LoggerConfig) {
    this.module = config?.module || 'app';
    this.enabled = config?.enabled !== undefined ? config.enabled : isLoggingEnabled();
  }

  /**
   * Formatea el mensaje con el módulo
   */
  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] ${level.toUpperCase()} (${this.module}): ${message}`;
  }

  /**
   * Log debug message (solo en development)
   */
  debug(message: string, ...args: any[]): void {
    if (!this.enabled) return;

    if (args.length > 0) {
      // eslint-disable-next-line no-console
      console.debug(this.formatMessage(LogLevel.DEBUG, message), ...args);
    } else {
      // eslint-disable-next-line no-console
      console.debug(this.formatMessage(LogLevel.DEBUG, message));
    }
  }

  /**
   * Log info message
   */
  info(message: string, ...args: any[]): void {
    if (!this.enabled) return;

    if (args.length > 0) {
      // eslint-disable-next-line no-console
      console.info(this.formatMessage(LogLevel.INFO, message), ...args);
    } else {
      // eslint-disable-next-line no-console
      console.info(this.formatMessage(LogLevel.INFO, message));
    }
  }

  /**
   * Log warning message
   */
  warn(message: string, ...args: any[]): void {
    if (!this.enabled) return;

    if (args.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(this.formatMessage(LogLevel.WARN, message), ...args);
    } else {
      // eslint-disable-next-line no-console
      console.warn(this.formatMessage(LogLevel.WARN, message));
    }
  }

  /**
   * Log error message
   */
  error(message: string, error?: Error | any, ...args: any[]): void {
    if (!this.enabled) return;

    const allArgs = error ? [error, ...args] : args;

    if (allArgs.length > 0) {
      // eslint-disable-next-line no-console
      console.error(this.formatMessage(LogLevel.ERROR, message), ...allArgs);
    } else {
      // eslint-disable-next-line no-console
      console.error(this.formatMessage(LogLevel.ERROR, message));
    }
  }

  /**
   * Log con objeto de datos estructurados
   */
  log(level: LogLevel, message: string, data?: any): void {
    if (!this.enabled) return;

    const formattedMessage = this.formatMessage(level, message);

    switch (level) {
      case LogLevel.DEBUG:
        // eslint-disable-next-line no-console
        data ? console.debug(formattedMessage, data) : console.debug(formattedMessage);
        break;
      case LogLevel.INFO:
        // eslint-disable-next-line no-console
        data ? console.info(formattedMessage, data) : console.info(formattedMessage);
        break;
      case LogLevel.WARN:
        // eslint-disable-next-line no-console
        data ? console.warn(formattedMessage, data) : console.warn(formattedMessage);
        break;
      case LogLevel.ERROR:
        // eslint-disable-next-line no-console
        data ? console.error(formattedMessage, data) : console.error(formattedMessage);
        break;
    }
  }

  /**
   * Crea un child logger con un submódulo
   */
  child(submodule: string): Logger {
    return new Logger({
      module: `${this.module}:${submodule}`,
      enabled: this.enabled
    });
  }
}

/**
 * Create a logger instance
 */
export const createLogger = (module?: string): Logger => {
  return new Logger({ module });
};

/**
 * Default logger instance
 */
export const logger = createLogger();

/**
 * Export helper para verificar si logging está habilitado
 */
export const isDevMode = isLoggingEnabled;
